import React from "react";
import useOutsideClick from "../../hooks/useOutsideClick";
import { ReactComponent as IconHamburger } from "../../assets/icons/icon_hamburger.svg";
import { ReactComponent as IconHouse } from "../../assets/icons/icon_house.svg";
import { ReactComponent as IconKey } from "../../assets/icons/icon_key.svg";
import { ReactComponent as IconUser } from "../../assets/icons/icon_user.svg";
import { ReactComponent as IconTicket } from "../../assets/icons/icon_ticket.svg";
import { ReactComponent as IconCross } from "../../assets/icons/icon_cross.svg";
import * as styles from "./styles";
import { Box } from "@hooplab/ui-components";
import { classes } from "typestyle";
import { FormattedMessage } from "react-intl";
import { paths } from "../../App";
import { Organization } from "../../types";
import OrganizationImage from "../OrganizationImage";
import colors from "../../styles/colors";
import { getDomain } from "../../environment";

const NavigationMenu: React.FC<{ isLoggedIn: boolean; organization: Organization }> = ({
  isLoggedIn,
  organization,
}) => {
  const [hamburgerMenuOpen, setHamburgerMenuOpen] = React.useState(false);
  const hamburgerMenuTogglerRef = React.useRef(null);
  useOutsideClick(hamburgerMenuTogglerRef, () => setHamburgerMenuOpen(false));
  return (
    <div
      ref={hamburgerMenuTogglerRef}
      className={styles.hamburgerMenuComp}
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        position: "relative",
      }}
    >
      <button
        className={styles.hamburgerMenuToggler}
        onClick={() => setHamburgerMenuOpen((open) => !open)}
      >
        <IconCross height="13" style={{ display: hamburgerMenuOpen ? "block" : "none" }} />
        <IconHamburger height="14" style={{ display: hamburgerMenuOpen ? "none" : "block" }} />
      </button>
      <Box
        additionalCls={classes(styles.hamburgerMenu, hamburgerMenuOpen && styles.hamburgerMenuOpen)}
      >
        <div className={styles.hamburgerMenuInner}>
          <a className={styles.hamburgerMenuOuter} href={paths.organization()}>
            <div className={styles.hamburgerMenuItem}>
              <OrganizationImage organization={organization} />
              <div className={styles.hamburgerMenuItemInner}>
                <span>{organization.name}</span>
                <FormattedMessage
                  tagName={"span"}
                  id="components.Header.NavigationMenu.organizerPageInfo"
                  defaultMessage={"Full program"}
                />
              </div>
            </div>
          </a>
          {window.ORGANIZATION_REGION === "NO" && (
            <a className={styles.hamburgerMenuOuter} href={`https://www.${getDomain()}`}>
              <div className={styles.hamburgerMenuItem}>
                <div>
                  <span className={styles.icon} style={{ height: 32, width: 32 }}>
                    <IconHouse height="16" />
                  </span>
                </div>
                <div className={styles.hamburgerMenuItemInner}>
                  <FormattedMessage
                    tagName={"span"}
                    id="components.Header.NavigationMenu.hooplaNo"
                    defaultMessage={"Hoopla front page"}
                  />
                  <FormattedMessage
                    tagName={"span"}
                    id="components.Header.NavigationMenu.hooplaNoInfo"
                    defaultMessage={"All our events"}
                  />
                </div>
              </div>
            </a>
          )}
          <div className={styles.divider}>
            <FormattedMessage
              tagName={"span"}
              id="components.Header.NavigationMenu.otherPages"
              defaultMessage={"Other pages"}
            />
          </div>
          <a className={styles.hamburgerMenuOuter} href="/organizer">
            <div className={styles.hamburgerMenuItem}>
              <div>
                <span
                  className={styles.icon}
                  style={{
                    height: 32,
                    width: 32,
                    backgroundColor: isLoggedIn ? "#005CB4" : colors.grey[400],
                  }}
                >
                  <IconKey height="16" />
                </span>
              </div>
              <div className={styles.hamburgerMenuItemInner}>
                {isLoggedIn ? (
                  <>
                    <FormattedMessage
                      tagName={"span"}
                      id="components.Header.NavigationMenu.organizerLogin"
                      defaultMessage={"Organizer: {organizerName}"}
                      values={{
                        organizerName: organization.name,
                      }}
                    />
                    <FormattedMessage
                      tagName={"span"}
                      id="components.Header.NavigationMenu.organizerLoginInfo"
                      defaultMessage={"Go to organizer page"}
                    />
                  </>
                ) : (
                  <>
                    <FormattedMessage
                      tagName={"span"}
                      id="components.Header.NavigationMenu.forOrganizer"
                      defaultMessage={"For organizer"}
                    />
                    <FormattedMessage
                      tagName={"span"}
                      id="components.Header.NavigationMenu.forOrganizerInfo"
                      defaultMessage={"Log in to organizer page"}
                    />
                  </>
                )}
              </div>
            </div>
          </a>
          <a
            className={styles.hamburgerMenuOuter}
            href={`https://my.${getDomain()}/?utm_source=sales_pages&utm_medium=hamburger_menu`}
          >
            <div className={styles.hamburgerMenuItem}>
              <div>
                <span
                  className={styles.icon}
                  style={{
                    height: 32,
                    width: 32,
                  }}
                >
                  <IconUser height="16" />
                </span>
              </div>
              <div className={styles.hamburgerMenuItemInner}>
                <FormattedMessage
                  tagName={"span"}
                  id="components.Header.NavigationMenu.myPage"
                  defaultMessage={"My page"}
                />
                <FormattedMessage
                  tagName={"span"}
                  id="components.Header.NavigationMenu.myPageInfo"
                  defaultMessage={"Find your tickets"}
                />
              </div>
            </div>
          </a>
          <a
            className={styles.hamburgerMenuOuter}
            href={
              window.ORGANIZATION_REGION === "NO"
                ? `https://www.${getDomain()}/arranger/?utm_source=sales_pages&utm_medium=hamburger_menu`
                : `https://www.${getDomain()}?utm_source=sales_pages&utm_medium=hamburger_menu`
            }
          >
            <div className={styles.hamburgerMenuItem}>
              <div>
                <span
                  className={styles.icon}
                  style={{
                    height: 32,
                    width: 32,
                  }}
                >
                  <IconTicket height="12" />
                </span>
              </div>
              <div className={styles.hamburgerMenuItemInner}>
                <FormattedMessage
                  tagName={"span"}
                  id="components.Header.NavigationMenu.sellTicketsWithHoopla"
                  defaultMessage={"Sell tickets with Hoopla"}
                />
                <FormattedMessage
                  tagName={"span"}
                  id="components.Header.NavigationMenu.sellTicketsWithHooplaInfo"
                  defaultMessage={"Get started with your sale"}
                />
              </div>
            </div>
          </a>
        </div>
      </Box>
    </div>
  );
};

export default NavigationMenu;
